import type { MouseEventHandler } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Play } from '@elfsight/icons';
import { MediaImage } from '@elfsight/widget-media';
import type { Rect } from 'react-use-rect';
import { useRect } from 'react-use-rect';
import { parseMediaUrl } from './parseUrl';
import { useVimeoThumbnails } from './useVimeoThumbnails';

interface VideoPreviewProps {
  videoUrl: string;
  iconSize?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  containerWidth?: string;
}

export const VideoPreview = ({
  videoUrl = '',
  iconSize = 24,
  onClick,
  containerWidth,
  ...forwardingProps
}: VideoPreviewProps) => {
  const [rect, setRect] = useState<Rect | null>(null);
  const [rectRef] = useRect(setRect, { resize: true });

  const isSmall = Number(rect?.width) < 320;

  const [source, videoId] = useMemo(() => parseMediaUrl(videoUrl), [videoUrl]);

  const youtubeThumbnails = {
    small: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
    large: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    small2x: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    large2x: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  };

  const vimeoThumbnails = useVimeoThumbnails(source, videoId);

  const sourceThumbnails = source
    ? {
        vimeo: vimeoThumbnails,
        youtube: youtubeThumbnails,
        null: null
      }[source]
    : null;

  if (!sourceThumbnails) return null;

  return (
    <Container {...forwardingProps} onClick={onClick} ref={rectRef}>
      <StyledPlayIcon _size={iconSize} />
      <MediaImage
        alt="Video Preview"
        ratio={9 / 16}
        src={isSmall ? sourceThumbnails.small : sourceThumbnails.large}
        srcSet={`${
          isSmall ? sourceThumbnails.small2x : sourceThumbnails.large2x
        } 2x`}
      />
    </Container>
  );
};

const Container = styled.div<{ onClick?: MouseEventHandler<HTMLDivElement> }>`
  width: 100%;
  position: relative;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};
`;

const StyledPlayIcon = styled(Play)<{ _size: number }>`
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.25));
  width: ${({ _size }) => _size}px;
  fill: #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
`;
